import { useState } from "preact/hooks";
import type { Message as Msg } from "../types";
import {
  customerEmail,
  customerName,
  escalationView,
  operatorName,
  prefilledMail,
} from "../store";
import { trackCalendlyClick, trackEscalationClick } from "../tracking";
import { linkify } from "../linkify";
import { CALENDAR, MAIL, PERSON } from "./icons";

// Vis kunden hvor booking-lenka går (f.eks. book.badstugutta.no) før hen trykker.
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

/**
 * Meldingstekst med klikkbare lenker. Bygger ekte <a>-noder av segmentene
 * fra linkify() — teksten går ALDRI gjennom dangerouslySetInnerHTML, så
 * modell- og operatørinnhold kan ikke injisere markup.
 */
function Text({ children }: { children: string }) {
  return (
    <>
      {linkify(children).map((seg, i) =>
        seg.type === "link" ? (
          <a
            key={i}
            href={seg.href}
            title={seg.href}
            {...(seg.internal ? {} : { target: "_blank", rel: "noopener noreferrer" })}
          >
            {seg.label}
          </a>
        ) : (
          seg.value
        )
      )}
    </>
  );
}

export function Message({ msg }: { msg: Msg }) {
  // ALL hooks MÅ være øverst, før eventuelle early returns.
  // Per pattern i EscalationForm.tsx: ALDRI hooks etter early returns.
  const [draftText, setDraftText] = useState(msg.mailDraft || "");
  const [draftHidden, setDraftHidden] = useState(false);

  if (msg.role === "system") {
    return (
      <div class="ic-msg ic-msg-system" role="status">
        {msg.content}
      </div>
    );
  }

  if (msg.role === "operator") {
    return (
      <div class="ic-msg ic-msg-iskra ic-msg-operator">
        <div class="ic-operator-label">{PERSON} {operatorName.value || "Mattis"} · Badstugutta</div>
        <div><Text>{msg.content}</Text></div>
      </div>
    );
  }

  function handleCalendlyClick() {
    trackCalendlyClick({
      email: customerEmail.value || null,
      name: customerName.value || null,
    });
  }

  function handleEscalationClick() {
    trackEscalationClick({
      email: customerEmail.value || null,
      name: customerName.value || null,
    });
    escalationView.value = true;
  }

  function handleSendMailDraft() {
    trackEscalationClick({
      email: customerEmail.value || null,
      name: customerName.value || null,
    });
    prefilledMail.value = draftText;
    escalationView.value = true;
  }

  const showEscalationCta =
    msg.intent === "static_fallback" || msg.intent === "escalate";

  const showMailDraft =
    msg.role === "iskra" && !!msg.mailDraft && !draftHidden;

  return (
    <div class={`ic-msg ic-msg-${msg.role}`}>
      <div><Text>{msg.content}</Text></div>
      {showMailDraft && (
        <div class="ic-mail-draft">
          <div class="ic-mail-draft-header">Forslag til e-post</div>
          <textarea
            class="ic-mail-draft-textarea"
            rows={6}
            value={draftText}
            onInput={(e) =>
              setDraftText((e.target as HTMLTextAreaElement).value)
            }
          />
          <div class="ic-mail-draft-actions">
            <button
              class="ic-mail-draft-send"
              onClick={handleSendMailDraft}
              type="button"
            >
              Send e-posten
            </button>
            <button
              class="ic-mail-draft-hide"
              onClick={() => setDraftHidden(true)}
              type="button"
            >
              Skjul
            </button>
          </div>
        </div>
      )}
      {((msg.intent === "push_calendly" && msg.calendlyLink) || showEscalationCta) && (
        <div class="ic-actions">
          {msg.intent === "push_calendly" && msg.calendlyLink && (
            <a
              class="ic-cta"
              href={msg.calendlyLink}
              target="_blank"
              rel="noopener"
              onClick={handleCalendlyClick}
            >
              {CALENDAR}
              <span class="ic-cta-text">
                Book en gratis prat
                {hostOf(msg.calendlyLink) && (
                  <span class="ic-cta-sub">Velg tid på {hostOf(msg.calendlyLink)}</span>
                )}
              </span>
            </a>
          )}
          {showEscalationCta && (
            <button class="ic-cta ic-cta-secondary" onClick={handleEscalationClick}>
              {MAIL}
              <span class="ic-cta-text">Send oss en e-post</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
